import React, { useEffect, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import { LoadingButton } from '@mui/lab';
import {
  Box,
  Button,
  Card,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import { Form, Formik, FormikProps } from 'formik';
import { Reorder, animate, motion, useMotionValue } from 'framer-motion';
import { useCreateUpdateProfileWidgets, useGetAllTagTypes } from '@juno/client-api';
import { ProfileWidgetTypeEnum, TagType } from '@juno/client-api/model';
import { ProfileWidget } from '@juno/client-api/model';
import { Container, DrawerFooter, DrawerHeader, RightExpandableDrawer } from '@juno/ui';
import { MutationAction, onMutation, useSettings } from '@juno/utils';

const ProfileAdmin: React.FC = () => {
  const { site, profileWidgets, refetchProfileWidgets } = useSettings();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [newTagType, setNewTagType] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const { data: tagTypes } = useGetAllTagTypes(site?.id || '');

  const createUpdateProfileWidgets = useCreateUpdateProfileWidgets(
    onMutation(MutationAction.UPDATE, 'Profile Widgets', () => {}),
  );

  const handleClose = () => {
    setDrawerOpen(false);
    setNewTagType('');
  };

  return (
    <Container>
      <Formik
        initialValues={profileWidgets || ([] as ProfileWidget[])}
        onSubmit={async (values, { resetForm }) => {
          setIsSaving(true);
          try {
            await createUpdateProfileWidgets.mutateAsync({ siteId: site?.id || '', data: values });
            refetchProfileWidgets && (await refetchProfileWidgets());
          } catch (error) {
            console.error(error);
          } finally {
            setIsSaving(false);
            resetForm({ values });
          }
        }}
        enableReinitialize
      >
        {(formik) => {
          return (
            <ProfileAdminInner
              newTagType={newTagType}
              setNewTagType={setNewTagType}
              formik={formik}
              tagTypes={tagTypes || []}
              setDrawerOpen={setDrawerOpen}
              drawerOpen={drawerOpen}
              handleClose={handleClose}
              isSaving={isSaving}
            />
          );
        }}
      </Formik>
    </Container>
  );
};

export default ProfileAdmin;

interface ProfileAdminInnerProps {
  formik: FormikProps<ProfileWidget[]>;
  setDrawerOpen: (open: boolean) => void;
  tagTypes: TagType[];
  drawerOpen: boolean;
  handleClose: () => void;
  isSaving: boolean;
  newTagType: string;
  setNewTagType: (tagType: string) => void;
}
const ProfileAdminInner: React.FC<ProfileAdminInnerProps> = ({
  formik,
  setDrawerOpen,
  tagTypes,
  drawerOpen,
  handleClose,
  isSaving,
  newTagType,
  setNewTagType,
}) => {
  const { values, setValues, isSubmitting, dirty } = formik;
  const handleReorder = (newOrder: ProfileWidget[]) => {
    setValues(newOrder.map((item, i) => ({ ...item, order: i + 1 })));
  };

  const height = useMotionValue(64 * values.length);

  useEffect(() => {
    animate(height, 64 * values.length, { type: 'tween', duration: 0.3 });
  }, [values.length]);

  return (
    <Form>
      <Card sx={{ p: 2, mt: 2, mb: 4 }}>
        <Typography variant='h5'>Profile</Typography>
        <Divider sx={{ my: 2 }} />
        <Typography variant='body2' sx={{ mb: 4 }}>
          Edit tag types to be shown at the bottom of user profiles. These will each appear as a
          card showing the tags of that type the user has. The user will be able to add or remove
          tags of this type from their profile.
        </Typography>
        <Stack direction='row' justifyContent={'space-between'}>
          <Box minWidth='50%'>
            <Button
              onClick={() => setDrawerOpen(true)}
              variant='contained'
              sx={{ mb: 2 }}
              aria-label={'Add a profile widget'}
            >
              Add Profile Widget
            </Button>
            <motion.div style={{ height }}>
              <Reorder.Group
                axis='y'
                values={values}
                onReorder={handleReorder}
                as='div'
                style={{ listStyleType: 'none' }}
              >
                {values
                  .sort((a, b) => (a.order || 0) - (b.order || 0))
                  .map((item, index) => (
                    <Reorder.Item value={item} key={`${item.id || item.tag_type.id}`}>
                      <Stack
                        direction='row'
                        alignItems='center'
                        sx={{
                          border: '1px solid rgba(0,0,0,.1)',
                          mt: 1.25,
                          mb: 1.25,
                          paddingX: 2,
                          paddingY: 1,
                          background: 'rgba(0,0,0,.02)',
                          borderRadius: 2,
                          cursor: 'grab',
                          '&:hover': {
                            background: 'rgba(0,0,0,.04)',
                          },
                        }}
                        justifyContent='space-between'
                      >
                        <Stack direction='row' alignItems='center'>
                          <Stack>
                            <Typography variant='subtitle1' sx={{ ml: 1 }}>
                              {tagTypes?.find((tt) => tt.id === item.tag_type.id)?.value}
                            </Typography>
                          </Stack>
                        </Stack>

                        <Stack direction='row'>
                          <IconButton
                            onClick={() =>
                              setValues((old) =>
                                old
                                  .filter((pw) => pw.id !== item.id)
                                  .map((pw, i) => {
                                    return { ...pw, order: i + 1 };
                                  }),
                              )
                            }
                            size='small'
                            aira-label='Remove profile widget'
                          >
                            <CloseIcon />
                          </IconButton>
                        </Stack>
                      </Stack>
                    </Reorder.Item>
                  ))}
              </Reorder.Group>
            </motion.div>
          </Box>
        </Stack>
        <Box textAlign='right'>
          <LoadingButton
            loading={isSaving}
            variant='contained'
            type='submit'
            disabled={!dirty}
            sx={{ mt: 3 }}
            aria-label='Save profile widgets'
          >
            Save
          </LoadingButton>
        </Box>
      </Card>
      <RightExpandableDrawer open={drawerOpen} onClose={handleClose}>
        <DrawerHeader title={'Create Profile Widget'} />
        <FormControl fullWidth size='small'>
          <InputLabel id='tag-type-select-label' sx={{ background: 'white' }}>
            Tag Type
          </InputLabel>
          <Select
            labelId='tag-type-select-label'
            id='tag-type-select'
            value={newTagType}
            label='Age'
            onChange={(event) => setNewTagType(event.target.value)}
            aria-label='Select tag type'
          >
            {tagTypes
              ?.filter((tt) => !values.find((v) => v.tag_type.id === tt.id))
              .map((tt) => (
                <MenuItem key={tt.id} value={tt.id}>
                  {tt.value}
                </MenuItem>
              ))}
            {tagTypes?.filter((tt) => !values.find((v) => v.tag_type.id === tt.id)).length ===
              0 && (
              <MenuItem disabled value={''}>
                No more tag types available
              </MenuItem>
            )}
          </Select>
        </FormControl>
        <DrawerFooter
          onCancel={handleClose}
          disabled={isSubmitting || !newTagType}
          confirmButtonText='Add'
          onSubmit={() => {
            const tagType = tagTypes?.find((tt) => tt.id === newTagType);
            if (!tagType) return;
            setValues([
              ...values,
              {
                id: '',
                tag_type: tagType,
                order: values.length + 1,
                type: ProfileWidgetTypeEnum.tag_type,
              },
            ]);
            setDrawerOpen(false);
          }}
          submitting={isSubmitting}
        />
      </RightExpandableDrawer>
    </Form>
  );
};
